<template>
  <div class="rs-form-tiptap-wrapper">
    <label v-if="label" class="d-block">
      {{ label }}
    </label>
    <div
      v-if="editor"
      class="rs-form-tiptap border rounded rs-bg-white d-flex flex-column"
      :style="{ height: computedHeight, position: 'relative' }"
    >
      <RsPartialTipTapToolbar
        v-if="!readOnly"
        :editor="(editor as any)"
        :format="format"
        :is-full-screen="isFullScreen"
        :on-full-screen-toggle="handleFullScreenToggle"
        :on-image-add="uploadEditorFile"
        :on-source-mode-toggle="handleSourceModeToggle"
        :on-file-upload-start="() => setBusy(true)"
        :on-file-upload-success="() => setBusy(false)"
        :on-file-upload-error="() => setBusy(false)"
      />

      <div v-show="!isSourceMode" class="flex-fill" :style="{ overflow: 'hidden' }">
        <component :is="'style'" v-if="styles.length > 0">
          {{ styles }}
        </component>
        <EditorContent :key="styles.length" :editor="(editor as any)" class="border-top border-bottom rs-bg-white h-100" />
      </div>

      <div v-show="isSourceMode" class="flex-fill source-mode border-top">
        <textarea
          v-model="sourceContent"
          class="form-control h-100 border-0"
        />
      </div>

      <div v-if="busy" class="busy-mode">
        <div class="h-100 d-flex align-items-center justify-content-center">
          <i class="far fa-spinner fa-spin fa-2x rs-text-primary" />
        </div>
      </div>

      <RsModalTipTapFullScreen
        v-model="isFullscreen"
        :content="fullScreenContent"
        :format="format"
        @submit="handleFullScreenSubmit"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { EditorContent, Editor } from '@tiptap/vue-3';
import RsPartialTipTapToolbar from '../RsPartialTipTapToolbar.vue';
import RsModalTipTapFullScreen from '../RsModalTipTapFullScreen.vue';
import { FileHandlerExtension, uploadEditorFile } from '@/shared/tiptap/extensions/file-handler';
import { extensions } from '@/shared/tiptap/consts';
import { decodeHtml, encodeHtml, prettifyHtml } from '@/shared';
import { TTipTapFormat } from '@/shared/tiptap/types';
import { formatHTML } from '@/shared/tiptap/dom-format';

export default defineComponent({
  components: {
    EditorContent,
    RsPartialTipTapToolbar,
    RsModalTipTapFullScreen,
  },

  props: {
    id: {
      type: String,
      required: false,
    },

    label: {
      type: String,
      required: false,
    },

    /**
     * Editor HTML. Part of v-model sucle
     */
    value: {
      type: String,
      default: '',
    },

    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: String,
      default: undefined,
    },

    /**
     * Editor's readonly state.
     */
    readOnly: {
      type: Boolean,
      default: false,
    },

    /**
     * Determines what controls are shown on the toolbar.
     * Options are `'full' | 'basic' | 'tiny'`
     */
    format: {
      type: String as PropType<TTipTapFormat>,
      default: 'full',
    },

    /**
     * Editor's height.
     */
    height: {
      type: String,
      default: '',
    },

    /**
     * If in full screen mode, this prop is fed to the toolbar
     * to show / hide full screen toggle controls.
     */
    isFullScreen: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'update:modelValue'],

  data() {
    return {
      editor: null as Editor | null,
      isSourceMode: false,
      isFullscreen: false,
      sourceContent: '',
      sourceContentTimer: undefined as number | undefined,
      busy: false,
      fullScreenContent: '',
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },

    /**
     * Returns editor height if defined, else falls back to default height based on `format`.
     */
    computedHeight() {
      const formatDefaults = {
        basic: '225px',
        full: '500px',
        tiny: '175px',
      };

      return this.height || formatDefaults[this.format];
    },

    /** Dynamically render style tags found in a HTML content */
    styles() {
      const html = this.getHTML(false);
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const styles = [ ...doc.querySelectorAll('style') ];
      return styles.map((s) => s.textContent).join('').trim();
    },
  },

  watch: {
    boundValue(newVal: string) {
      this.setEditorValue(newVal, true);
    },

    readOnly(newVal: boolean) {
      this.editor?.setEditable(newVal);
    },

    /**
     * Pushes edits made in source mode back into the editor, debounced.
     *
     * The debounce is required, not a nicety: `setEditorValue` reparses the string through
     * ProseMirror, which normalises and repairs partial markup. Running that per keystroke rewrites
     * half-typed tags out from under the user (and thrashes the parent v-model, since `setContent`
     * fires `onUpdate`). The `BFormTextarea` this replaced provided the same 200ms window via its
     * `debounce` prop.
     */
    sourceContent(newVal: string, oldVal: string) {
      if (newVal === oldVal) {
        return;
      }

      window.clearTimeout(this.sourceContentTimer);
      this.sourceContentTimer = window.setTimeout(() => this.setEditorValue(newVal, false), 200);
    },
  },

  mounted() {
    // Need TS ignore due to a typing in `Editor` that declares a `componentInstance`, resulting in TS throwing
    // an excessive depth issue. This issue does not actually occur.
    // @ts-ignore
    this.editor = new Editor({
      content: decodeHtml(this.boundValue),
      editable: !this.readOnly,

      onUpdate: () => {
        this.emitChanges();
      },

      extensions: [
        ...extensions,
        FileHandlerExtension(this as any),
      ],
    });
  },

  // Do not change this one to beforeDestroy, been there done that
  beforeUnmount() {
    window.clearTimeout(this.sourceContentTimer);
    this.editor?.destroy();
  },

  methods: {
    uploadEditorFile,

    /**
     * Simply emits the input event.
     * Extracted to separate method in case more is needed down the line.
     */
    emitChanges() {
      const out = this.getHTML(true);
      this.$emit('input', out);
      this.$emit('update:modelValue', out);
    },

    /**
     * Sets content of the editor.
     * @param html Usually an encoded HTML string.
     */
    setEditorValue(html: string, decode: boolean) {
      const decoded = decode ? decodeHtml(html) : html;

      if (this.getHTML(false) === decoded) {
        return;
      }

      this.editor?.commands.setContent(decoded, {
        errorOnInvalidContent: false,
      });
    },

    /**
     * Toggles busy state to show a spinner
     * @param value
     */
    setBusy(value: boolean) {
      this.busy = value;
    },

    /**
     * Returns HTML value encoded, whether from source content or the editor itself.
     */
    getHTML(encode: boolean) {
      const value = this.editor?.getHTML();

      if (!value) {
        return '';
      }

      const formatted = formatHTML(value);

      return encode ? encodeHtml(formatted) : formatted;
    },

    /**
     * Toggles source mode by replacing the editor with a <textarea> element
     */
    handleSourceModeToggle(state: boolean) {
      const html = prettifyHtml(this.getHTML(false));
      this.isSourceMode = state;

      if (state && html !== this.sourceContent) {
        this.sourceContent = html;
      }

      if (!state) {
        this.editor?.commands.setContent(html);
      }
    },

    /**
     * Sets full screen mode to true.
     */
    handleFullScreenToggle() {
      this.isFullscreen = true;
      this.fullScreenContent = this.getHTML(true);
    },

    /**
     * Handles full screen editor changes and sets them to the current editor's content by calling the v-model `@input` cycle.
     * Value in callback is decoded. It can be used directly.
     */
    handleFullScreenSubmit(value: string) {
      this.isFullscreen = false;
      this.setEditorValue(value, false);
      this.emitChanges();
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";

.rs-form-tiptap-wrapper {
  margin-bottom: 1rem;

  .rs-form-tiptap {
    overflow: hidden;

    .busy-mode {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      z-index: 1;
      background: rgba(0, 0, 0, 0.1);
    }

    .source-mode {
      textarea {
        font-family: 'Courier New', Courier, monospace;

        &:focus {
          outline: none;
          box-shadow: none;
        }
      }
    }

    .tiptap {
      height: 100%;
      overflow: auto;
      padding: 0.5em;
      outline: none;

      * {
        font-family: Arial, sans-serif;
      }

      p {
        color: black;
        font-size: 14px;
        margin-bottom: 8px;
      }

      ul, ol {
        margin-left: 2em;
      }

      a, img {
        cursor: pointer;
      }

      p, ul, ol {
        margin-bottom: 0.5rem;
      }

      img {
        display: inline;
        height: auto;

        &.ProseMirror-selectednode {
          outline: 3px solid $primary-color;
        }
      }
      table {
        border-collapse: separate;
        border-color: #eee;
        border-style: solid;

        td,
        th {
          border: 1px solid #aaa;
          box-sizing: border-box;
          min-width: 1em;
          position: relative;
          vertical-align: top;

          > * {
            margin-bottom: 0;
          }
        }

        th {
          background-color: #eee;
          font-weight: bold;
          text-align: left;
        }
      }

      .tableWrapper {
        margin: 1.5rem 0;
        overflow-x: auto;
      }

      rs-tiptap-custom-styles {
        display: none;
      }
    }
  }
}
</style>
